import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { MessageCircle, X, Send, Sparkles } from 'lucide-react'
import { api } from '../lib/api'
import { getApiBase } from '../lib/utils'
import MarkdownMessage from './MarkdownMessage'
import VoiceRecorder from './VoiceRecorder'
import ThinkingBlock from './ThinkingBlock'

type InternalAgent = {
  id: number
  name: string
  subtitle?: string | null
  instructions?: string | null
  icon_url?: string | null
}

type Message = {
  role: 'user' | 'assistant'
  text: string
}

type Suggestion = {
  label: string
  question: string
}

type SupportConfig = {
  internal_widget_enabled: boolean
  internal_agent_id: number | null
  internal_agent: InternalAgent | null
  internal_widget_suggestions: Suggestion[]
}

export default function SupportWidget() {
  const [open, setOpen] = useState(false)
  const [config, setConfig] = useState<SupportConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [thinkingText, setThinkingText] = useState('')
  const [isThinking, setIsThinking] = useState(false)
  const [conversationId, setConversationId] = useState<number | null>(null)
  const [iconBlobUrl, setIconBlobUrl] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Get user info from localStorage
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null')
    } catch {
      return null
    }
  })()

  // Generate personalized greeting
  const getGreeting = () => {
    const firstName = user?.first_name || user?.name?.split(' ')[0]
    const useTu = user?.use_tu !== false // Default to "tu" (tutoiement)
    
    if (firstName) {
      return useTu 
        ? `Bonjour ${firstName}, comment puis-je t'aider ?`
        : `Bonjour ${firstName}, comment puis-je vous aider ?`
    }
    
    return useTu
      ? 'Comment puis-je t\'aider ?'
      : 'Comment puis-je vous aider ?'
  }

  // Fetch config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data } = await api.get('/global-config/internal-widget')
        setConfig(data)
        // Load icon if available
        if (data?.internal_agent?.icon_url) {
          try {
            const iconRes = await api.get(data.internal_agent.icon_url, { responseType: 'blob' })
            const url = URL.createObjectURL(iconRes.data as Blob)
            setIconBlobUrl(url)
          } catch {
            // Ignore icon load errors
          }
        }
      } catch {
        setConfig(null)
      } finally {
        setLoading(false)
      }
    }
    fetchConfig()
  }, [])

  // Cleanup blob URL
  useEffect(() => {
    return () => {
      if (iconBlobUrl) {
        URL.revokeObjectURL(iconBlobUrl)
      }
    }
  }, [iconBlobUrl])

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  const handleSend = useCallback(async () => {
    if (!input.trim() || streaming || !config?.internal_agent_id) return

    const userMessage = input.trim()
    setInput('')
    setMessages(prev => [...prev, { role: 'user', text: userMessage }])
    setStreaming(true)

    try {
      const token = localStorage.getItem('access_token')
      const baseUrl = getApiBase()
      const url = `${baseUrl}/chat/stream`
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          agent_id: config.internal_agent_id,
          question: userMessage,
          conversation_id: conversationId,
        }),
      })

      if (!response.ok) {
        throw new Error('Chat request failed')
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No reader')

      const decoder = new TextDecoder()
      let assistantText = ''
      let firstTokenReceived = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              
              // Gérer les messages de statut (patience)
              if (data.status_message && !firstTokenReceived) {
                setStatusMessage(data.status_message)
              }
              
              // Gérer les événements thinking (modèles avec <think>)
              if (data.thinking_started) {
                setIsThinking(true)
                setThinkingText('')
                setStatusMessage('Réflexion en cours…')
              }
              if (data.thinking) {
                setThinkingText(prev => prev + data.thinking)
              }
              if (data.thinking_done) {
                setIsThinking(false)
                setStatusMessage(null)
              }
              
              if (data.token) {
                if (!firstTokenReceived) {
                  firstTokenReceived = true
                  setStatusMessage(null)
                  setMessages(prev => [...prev, { role: 'assistant', text: data.token }])
                  assistantText = data.token
                } else {
                  assistantText += data.token
                  setMessages(prev => {
                    const updated = [...prev]
                    updated[updated.length - 1] = { role: 'assistant', text: assistantText }
                    return updated
                  })
                }
              }
              if (data.done) {
                if (data.conversation_id && !conversationId) {
                  setConversationId(data.conversation_id)
                }
                // Ajouter sources si présentes
                if (Array.isArray(data.sources) && data.sources.length) {
                  const srcs = '\n\n**Sources :**\n' + data.sources.map((s: any) => `- ${s.title ?? 'media ' + s.media_id}`).join('\n')
                  assistantText += srcs
                  setMessages(prev => {
                    const updated = [...prev]
                    updated[updated.length - 1] = { role: 'assistant', text: assistantText }
                    return updated
                  })
                }
              } else if (data.conversation_id && !conversationId) {
                setConversationId(data.conversation_id)
              }
            } catch {
              // Ignore parse errors
            }
          }
        }
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Une erreur est survenue. Veuillez réessayer.' }])
    } finally {
      setStreaming(false)
      setStatusMessage(null)
      setIsThinking(false)
    }
  }, [input, streaming, config, conversationId])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Don't render if disabled or no agent configured
  if (loading) return null
  if (!config?.internal_widget_enabled) return null

  const agent = config.internal_agent
  const isConfigured = !!agent

  return (
    <>
      {/* Header Button */}
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition-colors"
        title="Support Totemium"
      >
        <Sparkles className="w-4 h-4 text-emerald-400" />
        <span className="text-sm font-medium text-emerald-400">Support IA</span>
      </button>

      {/* Fullscreen Modal - Rendered via Portal */}
      {open && createPortal(
        <div className="fixed inset-0 z-[9999] bg-zinc-950/95 backdrop-blur-md">
          <div className="w-full h-full flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-5 border-b border-zinc-800/50 bg-transparent">
              <div className="flex items-center gap-3">
                {iconBlobUrl ? (
                  <img src={iconBlobUrl} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-blue-400" />
                  </div>
                )}
                <div>
                  <h2 className="text-base font-medium text-white">
                    {agent?.name || 'Assistant IA'}
                  </h2>
                  <p className="text-xs text-zinc-500">
                    {isConfigured 
                      ? (agent?.subtitle || 'Support Totemium')
                      : 'Non configuré - Admin > Configuration Globale'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-lg hover:bg-zinc-800/50 transition-colors"
              >
                <X className="w-5 h-5 text-zinc-500 hover:text-white" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto px-8 py-12">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center max-w-3xl mx-auto">
                  {iconBlobUrl ? (
                    <img src={iconBlobUrl} alt="" className="w-20 h-20 rounded-full object-cover mb-8" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center mb-8">
                      <Sparkles className="w-10 h-10 text-blue-400" />
                    </div>
                  )}
                  <h3 className="text-2xl font-semibold text-white mb-10">
                    {getGreeting()}
                  </h3>
                  
                  {/* Quick suggestions */}
                  {isConfigured && config.internal_widget_suggestions && config.internal_widget_suggestions.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-3">
                      {config.internal_widget_suggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setInput(suggestion.question)
                            setTimeout(() => handleSend(), 100)
                          }}
                          className="px-5 py-2.5 rounded-full border border-zinc-700/50 bg-zinc-800/30 text-sm text-zinc-300 hover:bg-zinc-800/60 hover:border-zinc-600 transition-all"
                        >
                          {suggestion.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4 max-w-4xl mx-auto">
                  {messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-2xl px-5 py-3.5 ${
                          msg.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : 'bg-zinc-800/80 text-zinc-100 border border-zinc-700/50'
                        }`}
                      >
                        <MarkdownMessage content={msg.text} className="text-sm leading-relaxed" />
                      </div>
                    </div>
                  ))}
                  
                  {isThinking && (
                    <div className="flex justify-start">
                      <ThinkingBlock content={thinkingText} isActive={true} />
                    </div>
                  )}
                  {streaming && !isThinking && (
                    <div className="flex justify-start">
                      <div className="flex items-center gap-3 rounded-2xl bg-zinc-800/80 border border-zinc-700/50 px-5 py-3.5">
                        {iconBlobUrl ? (
                          <img src={iconBlobUrl} alt="" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                            <Sparkles className="w-3 h-3 text-blue-400" />
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                          <span className="text-sm text-zinc-400">{statusMessage || 'Réponse en cours...'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input Area */}
            <div className="px-8 py-6 border-t border-zinc-800/50 bg-transparent">
              {!isConfigured ? (
                <div className="text-center text-zinc-500 text-sm py-2">
                  IA non configurée. Configurez Totemium dans Admin &gt; Configuration Globale
                </div>
              ) : (
                <div className="flex items-end gap-3 max-w-4xl mx-auto">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Appuyez sur Entrée pour envoyer, Shift+Entrée pour un saut de ligne"
                    className="flex-1 bg-zinc-900/50 border border-zinc-700/50 rounded-xl px-4 py-3 text-white placeholder-zinc-500 resize-none focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/20 transition-all"
                    rows={1}
                    disabled={streaming}
                  />
                  <VoiceRecorder
                    onTranscription={(text) => setInput(text)}
                    disabled={streaming}
                  />
                  <button
                    onClick={handleSend}
                    disabled={!input.trim() || streaming}
                    className="p-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-blue-500/20"
                  >
                    <Send className="w-5 h-5 text-white" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
